import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { EnemySystem, PISTOL_SOCKET } from '../enemies/EnemySystem';

/**
 * Temporary interactive pistol socket editor.
 * Open with `?calib=pistol` — orbit camera, click pistol, drag gizmo, copy values.
 */
export class PistolCalib {
  private readonly orbit: OrbitControls;
  private readonly gizmo: TransformControls;
  private readonly ray = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly panel: HTMLElement;
  private readonly valuesEl: HTMLElement;
  private readonly statusEl: HTMLElement;
  private readonly boxHelper: THREE.BoxHelper;
  private readonly onKey: (e: KeyboardEvent) => void;
  private readonly onPointerDown: (e: PointerEvent) => void;

  private enemyId = -1;
  private weapon: THREE.Object3D | null = null;
  private selected = false;
  private dragging = false;
  private active = false;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly dom: HTMLElement,
    private readonly enemies: EnemySystem,
  ) {
    this.orbit = new OrbitControls(this.camera, this.dom);
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = 0.08;
    this.orbit.target.set(0, 1.2, 0);
    this.orbit.enabled = false;

    this.gizmo = new TransformControls(this.camera, this.dom);
    this.gizmo.setMode('translate');
    this.gizmo.setSpace('local');
    this.gizmo.setSize(0.85);
    this.gizmo.enabled = false;
    this.gizmo.getHelper().visible = false;
    this.gizmo.addEventListener('dragging-changed', (ev) => {
      const dragging = Boolean((ev as { value?: boolean }).value);
      this.dragging = dragging;
      this.orbit.enabled = this.active && !dragging;
    });
    this.gizmo.addEventListener('change', () => this.refreshPanel());
    this.scene.add(this.gizmo.getHelper());

    this.boxHelper = new THREE.BoxHelper(new THREE.Object3D(), 0xffcc44);
    this.boxHelper.visible = false;
    this.scene.add(this.boxHelper);

    this.panel = document.getElementById('calib-panel') as HTMLElement;
    this.valuesEl = document.getElementById('calib-values') as HTMLElement;
    this.statusEl = document.getElementById('calib-status') as HTMLElement;

    document.getElementById('calib-copy')?.addEventListener('click', () => void this.copySnippet());
    document.getElementById('calib-reset')?.addEventListener('click', () => this.resetSocket());
    document.getElementById('calib-mode-translate')?.addEventListener('click', () =>
      this.setMode('translate'),
    );
    document.getElementById('calib-mode-rotate')?.addEventListener('click', () =>
      this.setMode('rotate'),
    );

    this.onKey = (e) => this.handleKey(e);
    this.onPointerDown = (e) => this.handlePointerDown(e);
  }

  get isActive(): boolean {
    return this.active;
  }

  start(): boolean {
    const pistol = this.enemies.debugList().find((e) => e.kind === 'pistol');
    if (!pistol) {
      console.error('[PistolCalib] no pistol enemy spawned');
      return false;
    }

    this.enemyId = pistol.id;
    for (const e of this.enemies.debugList()) {
      this.enemies.debugFreeze(e.id, true);
    }
    this.enemies.debugResetPose(this.enemyId, Math.PI);
    this.enemies.debugForceAim(this.enemyId);

    this.weapon = this.enemies.debugGetWeapon(this.enemyId);
    const root = this.enemies.debugGetRoot(this.enemyId);
    if (!this.weapon || !root) {
      console.error('[PistolCalib] weapon/root missing');
      return false;
    }

    const focus = new THREE.Vector3();
    root.getWorldPosition(focus);
    focus.y += 1.25;
    this.orbit.target.copy(focus);
    this.camera.position.set(focus.x + 1.6, focus.y + 0.35, focus.z + 1.1);
    this.camera.lookAt(focus);
    this.orbit.update();

    this.active = true;
    this.orbit.enabled = true;
    this.panel.classList.remove('hidden');
    this.statusEl.textContent = 'Click the pistol to select it, then drag the gizmo.';

    window.addEventListener('keydown', this.onKey);
    this.dom.addEventListener('pointerdown', this.onPointerDown);

    // Auto-select so user can drag immediately
    this.selectWeapon(true);
    this.refreshPanel();
    return true;
  }

  update(): void {
    if (!this.active) return;
    if (!this.dragging) this.orbit.update();
    if (this.selected && this.weapon) {
      this.boxHelper.setFromObject(this.weapon);
      this.boxHelper.visible = true;
    }
    this.refreshPanel();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.dom.removeEventListener('pointerdown', this.onPointerDown);
    this.gizmo.detach();
    this.gizmo.dispose();
    this.orbit.dispose();
    this.scene.remove(this.gizmo.getHelper());
    this.scene.remove(this.boxHelper);
    this.panel.classList.add('hidden');
    this.active = false;
  }

  private setMode(mode: 'translate' | 'rotate'): void {
    this.gizmo.setMode(mode);
    document.getElementById('calib-mode-translate')?.classList.toggle('active', mode === 'translate');
    document.getElementById('calib-mode-rotate')?.classList.toggle('active', mode === 'rotate');
  }

  private handleKey(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.code === 'KeyG' || e.code === 'Digit1') this.setMode('translate');
    if (e.code === 'KeyR' || e.code === 'Digit2') this.setMode('rotate');
    if (e.code === 'KeyL') this.gizmo.setSpace(this.gizmo.space === 'local' ? 'world' : 'local');
    if (e.code === 'Escape') this.selectWeapon(false);
  }

  private handlePointerDown(e: PointerEvent): void {
    if (!this.active || e.button !== 0 || this.dragging) return;
    // Ignore clicks on the calib panel
    if ((e.target as HTMLElement | null)?.closest?.('#calib-panel')) return;

    const rect = this.dom.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.ray.setFromCamera(this.ndc, this.camera);

    if (!this.weapon) return;
    const hits = this.ray.intersectObject(this.weapon, true);
    if (hits.length > 0) {
      this.selectWeapon(true);
      this.statusEl.textContent = 'Pistol selected — drag gizmo. G=move R=rotate L=local/world.';
    }
  }

  private selectWeapon(on: boolean): void {
    this.selected = on;
    if (on && this.weapon) {
      this.gizmo.attach(this.weapon);
      this.gizmo.enabled = true;
      this.gizmo.getHelper().visible = true;
      this.boxHelper.setFromObject(this.weapon);
      this.boxHelper.visible = true;
      this.statusEl.textContent = 'SELECTED — drag axes to seat the pistol in the clasp.';
    } else {
      this.gizmo.detach();
      this.gizmo.enabled = false;
      this.gizmo.getHelper().visible = false;
      this.boxHelper.visible = false;
      this.statusEl.textContent = 'Click the pistol to select it.';
    }
  }

  private resetSocket(): void {
    if (this.enemyId < 0) return;
    this.enemies.debugSetWeaponSocket(
      this.enemyId,
      {
        x: PISTOL_SOCKET.position.x,
        y: PISTOL_SOCKET.position.y,
        z: PISTOL_SOCKET.position.z,
      },
      {
        x: PISTOL_SOCKET.rotation.x,
        y: PISTOL_SOCKET.rotation.y,
        z: PISTOL_SOCKET.rotation.z,
      },
    );
    this.refreshPanel();
    this.statusEl.textContent = 'Reset to current PISTOL_SOCKET constants.';
  }

  private refreshPanel(): void {
    if (!this.weapon || !this.valuesEl) return;
    const p = this.weapon.position;
    const r = this.weapon.rotation;
    r.order = 'YXZ';
    this.valuesEl.textContent = [
      'PISTOL_SOCKET (hand local)',
      `position: (${fmt(p.x)}, ${fmt(p.y)}, ${fmt(p.z)})`,
      `rotation YXZ: (${fmt(r.x)}, ${fmt(r.y)}, ${fmt(r.z)})`,
      `rotation deg: (${deg(r.x)}, ${deg(r.y)}, ${deg(r.z)})`,
      `space: ${this.gizmo.space}   mode: ${this.gizmo.mode}`,
      this.selected ? 'selection: ON (yellow box)' : 'selection: off',
    ].join('\n');
  }

  private async copySnippet(): Promise<void> {
    if (!this.weapon) return;
    const p = this.weapon.position;
    const r = this.weapon.rotation;
    r.order = 'YXZ';
    const text = [
      'export const PISTOL_SOCKET = {',
      `  position: new THREE.Vector3(${fmt(p.x)}, ${fmt(p.y)}, ${fmt(p.z)}),`,
      `  rotation: new THREE.Euler(${fmt(r.x)}, ${fmt(r.y)}, ${fmt(r.z)}, 'YXZ'),`,
      '};',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      this.statusEl.textContent = 'Copied PISTOL_SOCKET snippet to clipboard.';
    } catch {
      console.log(text);
      this.statusEl.textContent = 'Clipboard blocked — snippet logged to console.';
    }
  }
}

function fmt(n: number): string {
  return n.toFixed(3);
}

function deg(rad: number): string {
  return `${(rad * (180 / Math.PI)).toFixed(1)}°`;
}
